import React, { useEffect, useRef, useState } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../App';

const TYPE_LABEL = { residential: 'Жилое', non_residential: 'Нежилое' };
const fmtPhone = p => {
  if (!p) return '—';
  const d = p.replace(/\D/g, '');
  if (d.length === 11) return `+7(${d.slice(1,4)})${d.slice(4,7)}-${d.slice(7,9)}-${d.slice(9,11)}`;
  if (d.length === 10) return `+7(${d.slice(0,3)})${d.slice(3,6)}-${d.slice(6,8)}-${d.slice(8,10)}`;
  return p;
};
const TYPE_BADGE = { residential: 'badge-blue', non_residential: 'badge-yellow' };

// ─── Owner modal ─────────────────────────────────────────
function OwnerModal({ owner, onClose, onSave }) {
  const [form, setForm] = useState(owner || { name: '', phone: '', email: '', inn: '', notes: '' });
  const [err, setErr] = useState('');
  const s = f => setForm(p => ({ ...p, ...f }));

  const save = async () => {
    if (!form.name.trim()) return setErr('ФИО / наименование обязательно');
    try {
      if (owner?.id) await put(`/owners/${owner.id}`, form);
      else await post('/owners', form);
      onSave();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{owner?.id ? 'Редактировать собственника' : 'Новый собственник'}</div>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
        <div className="form-grid">
          <div className="form-group span-2">
            <label>ФИО / Наименование *</label>
            <input value={form.name} onChange={e => s({ name: e.target.value })} placeholder="Иванов Иван Иванович" />
          </div>
          <div className="form-group">
            <label>Телефон</label>
            <input value={form.phone || ''} onChange={e => s({ phone: e.target.value })} placeholder="+7 900 000 00 00" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input value={form.email || ''} onChange={e => s({ email: e.target.value })} placeholder="owner@example.com" />
          </div>
          <div className="form-group">
            <label>ИНН</label>
            <input value={form.inn || ''} onChange={e => s({ inn: e.target.value })} placeholder="771234567890" />
          </div>
          <div className="form-group span-2">
            <label>Примечания</label>
            <textarea value={form.notes || ''} onChange={e => s({ notes: e.target.value })} placeholder="Доп. информация..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={save}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// ─── Property modal ───────────────────────────────────────
function PropertyModal({ property, owners, onClose, onSave }) {
  const [form, setForm] = useState(property || {
    number: '', type: 'residential',
    address: 'Санкт-Петербург, Литейный пр-т, д. 13',
    owner_id: '', egfn_doc: ''
  });
  const [err, setErr] = useState('');
  const s = f => setForm(p => ({ ...p, ...f }));

  const save = async () => {
    if (!form.number.trim()) return setErr('Номер помещения обязателен');
    try {
      const payload = { ...form, owner_id: form.owner_id || null };
      if (property?.id) await put(`/properties/${property.id}`, payload);
      else await post('/properties', payload);
      onSave();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{property?.id ? 'Редактировать помещение' : 'Новое помещение'}</div>
        {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}
        <div className="form-grid">
          <div className="form-group">
            <label>Номер *</label>
            <input value={form.number} onChange={e => s({ number: e.target.value })} placeholder="Кв. 47" />
          </div>
          <div className="form-group">
            <label>Тип</label>
            <select value={form.type} onChange={e => s({ type: e.target.value })}>
              <option value="residential">Жилое</option>
              <option value="non_residential">Нежилое</option>
            </select>
          </div>
          <div className="form-group span-2">
            <label>Адрес</label>
            <input value={form.address || ''} onChange={e => s({ address: e.target.value })} />
          </div>
          <div className="form-group span-2">
            <label>Собственник</label>
            <select value={form.owner_id || ''} onChange={e => s({ owner_id: e.target.value || '' })}>
              <option value="">— не указан —</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="form-group span-2">
            <label>Выписка ЕГРН (название)</label>
            <input value={form.egfn_doc || ''} onChange={e => s({ egfn_doc: e.target.value })} placeholder="будет обновлено после загрузки файла" readOnly={!!form.id} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={save}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

// ─── Sublist of studios for a property ──────────────────
function StudiosSublist({ propertyId, allStudios }) {
  const studios = allStudios.filter(s => s.property_id === propertyId);
  if (studios.length === 0) return <div style={{ color: 'var(--text3)', fontSize: 13 }}>Студий нет</div>;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {studios.map(s => (
        <div key={s.id} style={{
          background: 'var(--bg3)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '8px 14px', minWidth: 110
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{s.number}</div>
          {s.tenant_name
            ? <>
                <div style={{ fontSize: 12, color: 'var(--green)' }}>Занята</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>{s.tenant_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                  {new Intl.NumberFormat('ru-RU').format(s.current_rate)} ₽/мес
                </div>
              </>
            : <div style={{ fontSize: 12, color: 'var(--text3)' }}>Свободна</div>
          }
        </div>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────
export default function PropertiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const egfnRef = useRef();

  const [tab, setTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [studios, setStudios] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [propModal, setPropModal] = useState(null);
  const [ownerModal, setOwnerModal] = useState(null);
  const [egfnUploading, setEgfnUploading] = useState(null); // property id being uploaded

  const loadAll = () => {
    get('/properties').then(setProperties).catch(() => {});
    get('/owners').then(setOwners).catch(() => {});
    get('/studios').then(setStudios).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  const toggleExpand = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const uploadEgfn = async (e, propId) => {
    const file = e.target.files[0];
    if (!file) return;
    setEgfnUploading(propId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/properties/${propId}/egfn`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      loadAll();
    } catch (e) { alert(e.message); }
    finally { setEgfnUploading(null); }
  };

  const deleteOwner = async (id) => {
    if (!window.confirm('Удалить собственника?')) return;
    try { await del(`/owners/${id}`); loadAll(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          {tab === 'properties' ? 'Помещения' : 'Собственники'}
          <span>{tab === 'properties' ? `${properties.length} объектов` : `${owners.length} записей`}</span>
        </h1>
        {isAdmin && tab === 'properties' && (
          <button className="btn btn-primary" onClick={() => setPropModal({})}>+ Добавить помещение</button>
        )}
        {isAdmin && tab === 'owners' && (
          <button className="btn btn-primary" onClick={() => setOwnerModal({})}>+ Добавить собственника</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button
          className={`btn ${tab === 'properties' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setTab('properties')}
        >🏢 Помещения</button>
        <button
          className={`btn ${tab === 'owners' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setTab('owners')}
        >👤 Собственники</button>
      </div>

      {/* ── Помещения ─── */}
      {tab === 'properties' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {properties.length === 0 && (
            <div className="card">
              <div className="empty"><div className="empty-icon">🏢</div><p>Помещений нет</p></div>
            </div>
          )}
          {properties.map(p => {
            const free = studios.filter(s => s.property_id === p.id && !s.active_contract_id).length;
            const busy = studios.filter(s => s.property_id === p.id && s.active_contract_id).length;
            return (
              <div className="card" key={p.id} style={{ padding: '16px 20px' }}>
                {/* Property header row */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                  onClick={() => toggleExpand(p.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 16 }}>{p.number}</strong>
                      <span className={`badge ${TYPE_BADGE[p.type]}`}>{TYPE_LABEL[p.type]}</span>
                      <span className="badge badge-green">{busy} занято</span>
                      <span className="badge badge-gray">{free} свободно</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {p.owner_name
                        ? <span>👤 {p.owner_name}</span>
                        : <span style={{ color: 'var(--text3)' }}>Собственник не указан</span>
                      }
                      {p.egfn_doc && (
                        <span style={{ color: 'var(--accent)' }}>📄 {p.egfn_doc}</span>
                      )}
                      <span style={{ color: 'var(--text3)' }}>{p.address}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    {isAdmin && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setPropModal(p); }}>✏️ Редактировать</button>
                        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }} onClick={e => e.stopPropagation()} title="Загрузить выписку ЕГРН">
                          {egfnUploading === p.id ? '⏳' : '📄 ЕГРН'}
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" style={{ display: 'none' }} onChange={e => uploadEgfn(e, p.id)} />
                        </label>
                        {p.egfn_doc && (
                          <a className="btn btn-ghost btn-sm" href={`/api/properties/${p.id}/egfn`} onClick={e => e.stopPropagation()} download title="Скачать ЕГРН">⬇</a>
                        )}
                      </>
                    )}
                    <span style={{ color: 'var(--text3)', fontSize: 14, userSelect: 'none' }}>
                      {expanded[p.id] ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {/* Expanded studios */}
                {expanded[p.id] && (
                  <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                    <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                      Студии ({p.studio_count})
                    </div>
                    <StudiosSublist propertyId={p.id} allStudios={studios} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Собственники ─── */}
      {tab === 'owners' && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ФИО / Наименование</th>
                  <th>Телефон</th>
                  <th>Email</th>
                  <th>ИНН</th>
                  <th>Примечания</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {owners.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
                      Собственников нет. Добавьте первого!
                    </td>
                  </tr>
                )}
                {owners.map(o => (
                  <tr key={o.id}>
                    <td><strong>{o.name}</strong></td>
                    <td style={{ color: 'var(--text2)' }}>{fmtPhone(o.phone)}</td>
                    <td style={{ color: 'var(--text2)' }}>{o.email || '—'}</td>
                    <td style={{ color: 'var(--text2)' }}>{o.inn || '—'}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text2)' }}>
                      {o.notes || '—'}
                    </td>
                    {isAdmin && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setOwnerModal(o)}>✏️</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteOwner(o.id)}>🗑</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {propModal !== null && (
        <PropertyModal
          property={propModal.id ? propModal : null}
          owners={owners}
          onClose={() => setPropModal(null)}
          onSave={() => { setPropModal(null); loadAll(); }}
        />
      )}
      {ownerModal !== null && (
        <OwnerModal
          owner={ownerModal.id ? ownerModal : null}
          onClose={() => setOwnerModal(null)}
          onSave={() => { setOwnerModal(null); loadAll(); }}
        />
      )}
    </div>
  );
}
